// Funnel v2 — the OTO decision buttons (OWNER DIRECTIVE 2026-06-12).
//
// Accept = free upgrade in ONE CLICK: the server records consent against the
// card SAVED at checkout (the LTO PaymentIntent's setup_future_usage). No
// charge, no card re-entry, no Stripe UI of any kind. Decline is a real,
// respectable path — the primary funnel metric is decliners who convert later,
// so this button must leave goodwill intact.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FunnelApiError, acceptTrialUpgrade } from '../../lib/funnel-api';
import { isDownsellEnabled } from '../../lib/downsell';
import { PrimaryCtaButton, QuietCtaButton } from './funnel/atoms';

export interface OtoButtonsProps {
  paymentIntentId: string;
}

export function OtoButtons({ paymentIntentId }: OtoButtonsProps) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function accept() {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await acceptTrialUpgrade(paymentIntentId);
      // One click — consent recorded against the saved card, nothing charged.
      // payment_intent_id carries through so /thanks can claim a same-device
      // session (issue #978) instead of sending the buyer to a login wall.
      navigate(`/thanks?consent=1&payment_intent_id=${encodeURIComponent(paymentIntentId)}`);
    } catch (err) {
      // Issue #1197: the upgrade is attached to the purchase made in THIS
      // browser session. If that proof is missing (a different tab or device,
      // or a long-since-closed purchase), "try again" would never work — say
      // what is actually true instead, and make clear the Blueprint is safe.
      if (err instanceof FunnelApiError && err.code === 'trial_consent_not_authorized') {
        // The same code covers both refusals the server can give: no proof of
        // purchase from this browser, and a proof that has aged out of the
        // window. "Open this page from that browser" would be false advice in
        // the second case, so the copy names both and promises nothing that
        // may not work.
        setError(
          'This upgrade attaches to the purchase you just made — in the browser you made it in, and only for a short window right after checkout. If you have switched browsers or devices, or some time has passed, it can no longer be added from here. Your Blueprint is paid for either way, and nothing has been charged.',
        );
      } else {
        setError('That did not go through. Give it a moment and try again — nothing has been charged.');
      }
      setSubmitting(false);
    }
  }

  function decline() {
    if (isDownsellEnabled()) {
      navigate('/course-offer');
      return;
    }
    navigate(`/thanks?payment_intent_id=${encodeURIComponent(paymentIntentId)}`);
  }

  return (
    <div className="grid gap-3">
      <PrimaryCtaButton type="button" onClick={accept} disabled={submitting} className="w-full">
        {submitting ? 'Attaching your upgrade…' : 'Add the free upgrade'}
      </PrimaryCtaButton>

      <p data-testid="consent-terms" className="text-xs leading-5 text-slate-500">
        No charge today, and nothing to re-enter — your trial uses the card from the purchase
        you just made. The 14-day VibeControl trial begins only when you click Set it up in
        VibeControl. Never activate, never billed. After the trial ends, the Standard plan is $97 per
        month — cancel anytime in your account settings.
      </p>

      {error ? (
        <p
          role="alert"
          className="rounded-md border border-brand-coral/25 bg-brand-coral/10 px-4 py-3 text-sm leading-6 text-brand-coral"
        >
          {error}
        </p>
      ) : null}

      <QuietCtaButton type="button" onClick={decline}>
        No thanks — just my Blueprint
      </QuietCtaButton>
    </div>
  );
}
